
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Supabase-Konfiguration
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Bestellung {
  id: number;
  bestellnummer: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  adresse: string;
  plz: string;
  ort: string;
  produkte: any[];
  gesamtpreis: number;
  status: string;
  bemerkungen: string;
  created_at: string;
}

export default function BestellungenKundenPage() {
  const [bestellungen, setBestellungen] = useState<Bestellung[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [selectedBestellungen, setSelectedBestellungen] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<'single' | 'multiple'>('single');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    loadBestellungen();
  }, []);

  const loadBestellungen = async () => {
    try {
      const { data, error } = await supabase
        .from('bestellungen')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBestellungen(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Bestellungen:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteBestellung = async (id: number) => {
    try {
      const { error } = await supabase
        .from('bestellungen')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setBestellungen(prev => prev.filter(b => b.id !== id));
      setSelectedBestellungen(prev => prev.filter(selectedId => selectedId !== id));
    } catch (error) {
      console.error('Fehler beim Löschen der Bestellung:', error);
      alert('Fehler beim Löschen der Bestellung');
    }
  };

  const deleteMultipleBestellungen = async () => {
    try {
      const { error } = await supabase
        .from('bestellungen')
        .delete()
        .in('id', selectedBestellungen);

      if (error) throw error;
      
      setBestellungen(prev => prev.filter(b => !selectedBestellungen.includes(b.id)));
      setSelectedBestellungen([]);
    } catch (error) {
      console.error('Fehler beim Löschen der Bestellungen:', error);
      alert('Fehler beim Löschen der Bestellungen');
    }
  };

  const updateStatus = async (id: number, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('bestellungen')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      
      setBestellungen(prev => prev.map(b => 
        b.id === id ? { ...b, status: newStatus } : b
      ));
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
    }
  };

  const exportToExcel = () => {
    const bestellungenToExport = selectedBestellungen.length > 0 
      ? filteredBestellungen.filter(b => selectedBestellungen.includes(b.id))
      : filteredBestellungen;

    const csvContent = [
      ['Bestellnummer', 'Datum', 'Vorname', 'Nachname', 'E-Mail', 'Telefon', 'Adresse', 'PLZ', 'Ort', 'Produkte', 'Gesamtpreis', 'Status', 'Bemerkungen'].join(';'),
      ...bestellungenToExport.map(b => [
        b.bestellnummer,
        new Date(b.created_at).toLocaleString('de-DE'),
        b.vorname,
        b.nachname,
        b.email,
        b.telefon,
        b.adresse,
        b.plz,
        b.ort,
        Array.isArray(b.produkte) ? b.produkte.map(p => `${p.quantity}x ${p.name}`).join(', ') : b.produkte,
        `${b.gesamtpreis.toFixed(2)} CHF`,
        b.status,
        b.bemerkungen || ''
      ].join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bestellungen_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBestellungen(filteredBestellungen.map(b => b.id));
    } else {
      setSelectedBestellungen([]);
    }
  };

  const handleSelectBestellung = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedBestellungen(prev => [...prev, id]);
    } else {
      setSelectedBestellungen(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const confirmDelete = (type: 'single' | 'multiple', id?: number) => {
    setDeleteTarget(type);
    setDeleteId(id || null);
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (deleteTarget === 'single' && deleteId) {
      await deleteBestellung(deleteId);
    } else if (deleteTarget === 'multiple') {
      await deleteMultipleBestellungen();
    }
    setShowDeleteConfirm(false);
    setDeleteTarget('single');
    setDeleteId(null);
  };

  const filteredBestellungen = bestellungen.filter(bestellung => {
    const matchesSearch = 
      bestellung.bestellnummer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bestellung.vorname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bestellung.nachname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bestellung.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'alle' || bestellung.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Statistiken berechnen
  const stats = {
    gesamt: bestellungen.length,
    neu: bestellungen.filter(b => b.status === 'neu').length,
    bearbeitung: bestellungen.filter(b => b.status === 'in_bearbeitung').length,
    erledigt: bestellungen.filter(b => b.status === 'erledigt').length,
    heute: bestellungen.filter(b => new Date(b.created_at).toDateString() === new Date().toDateString()).length,
    umsatz: bestellungen.reduce((sum, b) => sum + b.gesamtpreis, 0),
    durchschnitt: bestellungen.length > 0 ? bestellungen.reduce((sum, b) => sum + b.gesamtpreis, 0) / bestellungen.length : 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Bestellungen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bestellverwaltung</h1>
          <p className="text-gray-600">Verwalten Sie alle Kundenbestellungen</p>
        </div>

        {/* Statistiken */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-blue-600">{stats.gesamt}</div>
            <div className="text-sm text-gray-600">Gesamt</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-orange-600">{stats.neu}</div>
            <div className="text-sm text-gray-600">Neu</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-yellow-600">{stats.bearbeitung}</div>
            <div className="text-sm text-gray-600">Bearbeitung</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-green-600">{stats.erledigt}</div>
            <div className="text-sm text-gray-600">Erledigt</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-purple-600">{stats.heute}</div>
            <div className="text-sm text-gray-600">Heute</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-emerald-600">{stats.umsatz.toFixed(0)} CHF</div>
            <div className="text-sm text-gray-600">Umsatz</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-cyan-600">{stats.durchschnitt.toFixed(0)} CHF</div>
            <div className="text-sm text-gray-600">Ø Bestellung</div>
          </div>
        </div>

        {/* Filter und Aktionen */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Bestellungen durchsuchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="alle">Alle Status</option>
                  <option value="neu">Neu</option>
                  <option value="in_bearbeitung">In Bearbeitung</option>
                  <option value="erledigt">Erledigt</option>
                </select>
              </div>
              <div className="flex gap-2">
                {selectedBestellungen.length > 0 && (
                  <button
                    onClick={() => confirmDelete('multiple')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Löschen ({selectedBestellungen.length})
                  </button>
                )}
                <button
                  onClick={exportToExcel}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-file-excel-line"></i>
                  Excel Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bestellungen Tabelle */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedBestellungen.length === filteredBestellungen.length && filteredBestellungen.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bestellung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produkte
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preis
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBestellungen.map((bestellung) => (
                  <tr key={bestellung.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBestellungen.includes(bestellung.id)}
                        onChange={(e) => handleSelectBestellung(bestellung.id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {bestellung.bestellnummer}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{bestellung.vorname} {bestellung.nachname}</div>
                        <div className="text-gray-500">{bestellung.email}</div>
                        <div className="text-gray-500">{bestellung.telefon}</div>
                        <div className="text-gray-500">{bestellung.adresse}, {bestellung.plz} {bestellung.ort}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {Array.isArray(bestellung.produkte) ? (
                          <div className="space-y-1">
                            {bestellung.produkte.map((produkt, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                  {produkt.quantity}x
                                </span>
                                <span className="text-sm">{produkt.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div>{bestellung.produkte}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {bestellung.gesamtpreis.toFixed(2)} CHF
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={bestellung.status}
                        onChange={(e) => updateStatus(bestellung.id, e.target.value)}
                        className="text-sm border-0 bg-transparent focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                      >
                        <option value="neu">🟠 Neu</option>
                        <option value="in_bearbeitung">🟡 In Bearbeitung</option>
                        <option value="erledigt">🟢 Erledigt</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(bestellung.created_at).toLocaleString('de-DE')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => confirmDelete('single', bestellung.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Bestellung löschen"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredBestellungen.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Bestellungen gefunden</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'alle' 
                  ? 'Versuchen Sie andere Suchkriterien.' 
                  : 'Es sind noch keine Bestellungen eingegangen.'}
              </p>
            </div>
          )}
        </div>

        {/* Lösch-Bestätigung Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  Bestellung{deleteTarget === 'multiple' ? 'en' : ''} löschen
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                {deleteTarget === 'multiple' 
                  ? `Möchten Sie wirklich ${selectedBestellungen.length} Bestellungen löschen? Diese Aktion kann nicht rückgängig gemacht werden.`
                  : 'Möchten Sie diese Bestellung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
                }
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={executeDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Löschen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
